import React, { useEffect, useState, useCallback } from "react";
import API_BASE_URL from "../../../Constants";
import SearchIcon from "@mui/icons-material/Search";
import ViewProposal from "./ViewProposal";
import { proposalListFields } from "../../proposals/components/Proposals/config";
import { useAuth } from "../../../contexts/AuthContext";

const ProposalApprovalList = () => {
  const [proposals, setProposals] = useState([]);
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [options, setOptions] = useState({});

  const { user } = useAuth();

  useEffect(() => {
    if (statusFilter === "Approved") {
      setFilteredProposals(
        proposals.filter((proposal) =>
          ["Accepted", "Accepted - Change Order"].includes(
            proposal.proposalStatus
          )
        )
      );
    } else if (statusFilter === "Rejected") {
      setFilteredProposals(
        proposals.filter(
          (proposal) => proposal.proposalStatus === "Proposal Rejected"
        )
      );
    } else {
      // all other status except the above 2
      setFilteredProposals(
        proposals.filter(
          (proposal) =>
            !["Accepted", "Proposal Rejected"].includes(proposal.proposalStatus)
        )
      );
    }
  }, [proposals, statusFilter]);

  const handleOpenViewModal = (proposal, index) => {
    setSelectedProposal(proposal);
    setSelectedIndex(index);
    setIsViewOpen(true);
  };

  const handleNext = () => {
    if (selectedIndex < filteredProposals.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      setSelectedProposal(filteredProposals[selectedIndex + 1]);
    }
  };

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      setSelectedProposal(filteredProposals[selectedIndex - 1]);
    }
  };

  const handleCloseViewModal = () => setIsViewOpen(false);

  // Fetch Proposals
  const fetchProposals = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/proposals-and-jobs/proposals/${user?.id}/customer`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Network response was not ok");

      const data = await response.json();
      setProposals(Array.isArray(data) ? data : []);
      setFilteredProposals(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Fetch error: ", error);
      setProposals([]);
      setFilteredProposals([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Search Functionality
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value === "") {
      setFilteredProposals(proposals);
    } else {
      setFilteredProposals(
        proposals.filter((proposal) =>
          proposal.proposalNumber.toString().includes(e.target.value)
        )
      );
    }
  };

  return (
    <div className="mx-auto pl-4 pr-4 h-screen w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Proposals List</h2>
        <div className="flex justify-end gap-4">
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
            {/* Filter for Status */}

            <select
              className="px-3 py-2 bg-white outline-none"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>

            <div className="px-3 py-2 text-gray-500 cursor-pointer">
              <SearchIcon />
            </div>
            <input
              type="text"
              className="px-3 py-2 w-full outline-none"
              placeholder="Search by Proposal Number"
              value={searchQuery}
              onChange={handleSearchChange}
              readOnly={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {!isLoading ? (
        <table className="min-w-full bg-white rounded-lg shadow-lg overflow-visible border-2 border-gray-200 relative">
          <thead className="bg-gray-200">
            <tr>
              {proposalListFields.map((field) => (
                <th
                  key={field.key}
                  // className="px-4 py-2 text-left font-bold text-blue-600"

                  style={{
                    display: "table-cell",
                    // filterEnabled
                    // && !visibleColumns[field.name]? 'none' : 'table-cell',
                    cursor: "pointer",
                  }}
                  className={`px-4 py-2 text-left font-bold text-sss-blue-sel relative `}
                >
                  {/* ${!visibleColumns[field.name] && filterEnabled ? 'opacity-50' : ''} */}

                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {filteredProposals.map((proposal, index) => (
              <tr
                key={proposal.proposalNumber}
                className={`border-t ${
                  index % 2 === 0 ? "bg-gray-50" : "bg-white"
                } hover:bg-yellow-300`}
              >
                {proposalListFields.map((field) => {
                  if (field.key === "actions") {
                    return (
                      <td key={field.key} className="px-4 py-2 table-cell">
                        <button
                          className="text-blue-600"
                          onClick={() => handleOpenViewModal(proposal, index)}
                        >
                          View
                        </button>
                      </td>
                    );
                  }

                  let displayValue = "";

                  if (field.key === "proposalStatus") {
                    displayValue = statusFilter;
                  }
                  // Subkeys (nested object display)
                  else if (field.subKeys && Array.isArray(field.subKeys)) {
                    const parentValue = proposal[field.key] || {};
                    displayValue = field.subKeys
                      .map((subKey) => parentValue[subKey])
                      .filter(Boolean)
                      .join(" ");
                  }

                  // Select options
                  else if (field.options && Array.isArray(field.options)) {
                    const matchedOption = field.options.find(
                      (opt) => opt.key === proposal[field.key]
                    );
                    displayValue = matchedOption?.label || proposal[field.key];
                  }

                  // Datetime formatting
                  else if (field.type === "datetime-local") {
                    const rawDate = proposal[field.key];
                    if (rawDate) {
                      const dateObj = new Date(rawDate);
                      const pad = (n) => n.toString().padStart(2, "0");
                      const mm = pad(dateObj.getMonth() + 1);
                      const dd = pad(dateObj.getDate());
                      const yyyy = dateObj.getFullYear();
                      const formattedDate = `${mm}/${dd}/${yyyy}`;

                      if (field.isDateOnly) {
                        displayValue = formattedDate;
                      } else {
                        const hours = pad(dateObj.getHours());
                        const minutes = pad(dateObj.getMinutes());
                        displayValue = `${formattedDate} ${hours}:${minutes}`;
                      }
                    }
                  }
                  // Fallback: raw value
                  else {
                    displayValue = proposal[field.key];
                  }

                  return (
                    <td key={field.key} className="px-4 py-2 table-cell">
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="flex justify-center items-center mt-6">
          <p>Loading...</p>
        </div>
      )}

      {/* Modals */}
      <ViewProposal
        open={isViewOpen}
        handleClose={handleCloseViewModal}
        proposal={selectedProposal}
        setProposal={setSelectedProposal}
        setNext={handleNext}
        setPrevious={handlePrevious}
        allOptions={options}
        allProposals={proposals}
        updateAll={setProposals}
      />
    </div>
  );
};

export default ProposalApprovalList;
